'use client';

import * as React from 'react';

import { cn } from '../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoResize?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoResize = false, onInput, 'aria-invalid': ariaInvalid, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const invalid = ariaInvalid === true || ariaInvalid === 'true';

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null): void => {
        innerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref],
    );

    const resize = React.useCallback((): void => {
      const node = innerRef.current;
      if (!node) return;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    }, []);

    React.useEffect(() => {
      if (autoResize) resize();
    }, [autoResize, resize, props.value, props.defaultValue]);

    const handleInput = React.useCallback<
      NonNullable<React.TextareaHTMLAttributes<HTMLTextAreaElement>['onInput']>
    >(
      (event) => {
        if (autoResize) resize();
        onInput?.(event);
      },
      [autoResize, onInput, resize],
    );

    return (
      <textarea
        ref={setRefs}
        aria-invalid={ariaInvalid}
        onInput={handleInput}
        className={cn(
          'border-input bg-card ring-offset-background placeholder:text-muted-foreground-soft focus-visible:ring-ring/60 hover:border-border-hover flex min-h-20 w-full rounded-xl border px-3.5 py-2.5 text-[13px] transition-[background-color,border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          autoResize && 'resize-none overflow-hidden',
          invalid && 'border-destructive focus-visible:ring-destructive',
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';
